package com.upcominds.expedite.features.news

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.upcominds.expedite.data.mobilelogs.logMobileError
import com.upcominds.expedite.data.news.NewsRepository
import com.upcominds.expedite.dto.CommunityNewsDTO
import com.upcominds.expedite.dto.NewsCommentCreateRequest
import com.upcominds.expedite.dto.NewsReactionType
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class NewsUiState(
    val loading: Boolean = false,
    val error: String? = null,
    val items: List<CommunityNewsDTO> = emptyList(),
    val actionInProgressIds: Set<String> = emptySet()
)

class NewsViewModel(
    private val newsRepository: NewsRepository
) : ViewModel() {

    private var loadJob: Job? = null

    private val _uiState = MutableStateFlow(NewsUiState())
    val uiState: StateFlow<NewsUiState> = _uiState.asStateFlow()

    fun loadNews() {
        loadJob?.cancel()
        loadJob = viewModelScope.launch {
            _uiState.value = _uiState.value.copy(
                loading = true,
                error = null,
                items = emptyList()
            )
            try {
                val news = newsRepository.getMyNews()
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    items = news,
                    error = null
                )
            } catch (e: Exception) {
                if (e is CancellationException) throw e
                logMobileError("NewsViewModel.loadNews", e)
                _uiState.value = _uiState.value.copy(
                    loading = false,
                    error = e.message
                )
            }
        }
    }

    fun onLikeClicked(news: CommunityNewsDTO) {
        toggleReaction(news, NewsReactionType.LIKE)
    }

    fun onDislikeClicked(news: CommunityNewsDTO) {
        toggleReaction(news, NewsReactionType.DISLIKE)
    }

    fun addComment(newsId: String, text: String) {
        val normalized = text.trim()
        if (normalized.isBlank()) return

        runAction(newsId) {
            val added = newsRepository.addComment(newsId, NewsCommentCreateRequest(text = normalized))
            _uiState.value = _uiState.value.copy(
                items = _uiState.value.items.map { item ->
                    if (item.id == newsId) item.copy(comments = item.comments + added) else item
                }
            )
        }
    }

    fun submitSurveyAnswer(newsId: String, optionId: String) {
        runAction(newsId) {
            val updated = newsRepository.answerSurvey(newsId, optionId)
            replaceNews(updated)
        }
    }

    private fun toggleReaction(news: CommunityNewsDTO, selected: NewsReactionType) {
        val alreadySelected = news.myReaction == selected.name
        runAction(news.id) {
            val updated = if (alreadySelected) {
                newsRepository.clearReaction(news.id)
            } else {
                newsRepository.setReaction(news.id, selected)
            }
            replaceNews(updated)
        }
    }

    private fun replaceNews(updated: CommunityNewsDTO) {
        _uiState.value = _uiState.value.copy(
            items = _uiState.value.items.map { item ->
                if (item.id == updated.id) updated else item
            }
        )
    }

    private fun runAction(newsId: String, action: suspend () -> Unit) {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(
                actionInProgressIds = _uiState.value.actionInProgressIds + newsId,
                error = null
            )

            try {
                action()
            } catch (e: Exception) {
                logMobileError("NewsViewModel.runAction", e, "Failed news action for $newsId")
                _uiState.value = _uiState.value.copy(error = e.message)
            } finally {
                _uiState.value = _uiState.value.copy(
                    actionInProgressIds = _uiState.value.actionInProgressIds - newsId
                )
            }
        }
    }
}
